// Таймер подхода на время (планка, ходьба, велотренажёр). Как и таймер отдыха, считается от
// абсолютного времени старта в localStorage: сворачивание и перезагрузка его не сбрасывают.
// Цель — время, уже стоявшее в подходе (шаблон, план, прошлый раз): до неё отсчёт вниз, после —
// в плюс, на цели один раз вибрация. «Стоп» записывает фактическое время (actions.toggleSet).

import { useSyncExternalStore } from 'react'

const STORAGE_KEY = 'health.setTimer'
const LATE_VIBRATE_MS = 10_000
const VIBRATE_PATTERN = [300, 150, 300, 150, 300]

type Stored = {
  exercise_key: string
  set_id: string
  name: string
  started_at: number
  target_s: number | null
  alerted?: boolean
}

export type SetTimerOptions = {
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  key?: string
  now?: () => number
  vibrate?: (pattern: number[]) => unknown
}

export type SetTimerSnapshot = {
  running: boolean
  exerciseKey: string | null
  setId: string | null
  name: string
  /** Прошло с начала, целых секунд. */
  elapsed: number
  /** Цель, секунд; null — просто секундомер. */
  target: number | null
}

const IDLE: SetTimerSnapshot = { running: false, exerciseKey: null, setId: null, name: '', elapsed: 0, target: null }

function defaultStorage(): SetTimerOptions['storage'] {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage
  } catch {
    return undefined
  }
}

function defaultVibrate(pattern: number[]): unknown {
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return false
  return navigator.vibrate(pattern)
}

export function createSetTimer(options: SetTimerOptions = {}) {
  const storage = options.storage ?? defaultStorage()
  const key = options.key ?? STORAGE_KEY
  const now = options.now ?? (() => Date.now())
  const vibrate = options.vibrate ?? defaultVibrate
  const listeners = new Set<() => void>()
  let snapshot: SetTimerSnapshot = IDLE

  const read = (): Stored | null => {
    try {
      const raw = storage?.getItem(key)
      if (!raw) return null
      const v = JSON.parse(raw) as Partial<Stored>
      if (typeof v.exercise_key !== 'string' || typeof v.set_id !== 'string' || typeof v.started_at !== 'number') return null
      return {
        exercise_key: v.exercise_key,
        set_id: v.set_id,
        name: typeof v.name === 'string' ? v.name : '',
        started_at: v.started_at,
        target_s: typeof v.target_s === 'number' && v.target_s > 0 ? v.target_s : null,
        alerted: v.alerted === true,
      }
    } catch {
      return null
    }
  }
  const write = (value: Stored | null) => {
    try {
      if (value) storage?.setItem(key, JSON.stringify(value))
      else storage?.removeItem(key)
    } catch {
      // хранилище недоступно (приватный режим) — таймер живёт до перезагрузки
    }
  }

  let state = read()

  const elapsedOf = (s: Stored) => Math.max(0, Math.floor((now() - s.started_at) / 1000))

  const refresh = () => {
    const next: SetTimerSnapshot = state
      ? {
          running: true,
          exerciseKey: state.exercise_key,
          setId: state.set_id,
          name: state.name,
          elapsed: elapsedOf(state),
          target: state.target_s,
        }
      : IDLE
    if (
      next.running !== snapshot.running ||
      next.setId !== snapshot.setId ||
      next.elapsed !== snapshot.elapsed ||
      next.target !== snapshot.target
    ) {
      snapshot = next
      for (const listener of listeners) listener()
    }
  }

  const timer = {
    /** Запустить подход с этого момента; цель — секунд или null. */
    start(exerciseKey: string, setId: string, name: string, target: number | null) {
      state = {
        exercise_key: exerciseKey,
        set_id: setId,
        name,
        started_at: now(),
        target_s: target && target > 0 ? Math.round(target) : null,
      }
      write(state)
      refresh()
    },
    /** Остановить. Возвращает, сколько секунд прошло (с округлением), или null, если не шёл. */
    stop(): number | null {
      if (!state) return null
      const seconds = Math.max(1, Math.round((now() - state.started_at) / 1000))
      state = null
      write(null)
      refresh()
      return seconds
    },
    /** Идёт ли таймер этого подхода. */
    isFor(setId: string): boolean {
      return state?.set_id === setId
    },
    /** Когда будет достигнута цель (мс), если она есть. */
    targetAt(): number | null {
      return state?.target_s ? state.started_at + state.target_s * 1000 : null
    },
    /** На цели — вибрация один раз. Вызывается по интервалу. */
    tick() {
      if (state?.target_s && !state.alerted && elapsedOf(state) >= state.target_s) {
        if (now() - (state.started_at + state.target_s * 1000) <= LATE_VIBRATE_MS) vibrate(VIBRATE_PATTERN)
        state = { ...state, alerted: true }
        write(state)
      }
      refresh()
    },
    snapshot(): SetTimerSnapshot {
      return snapshot
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
  refresh()
  return timer
}

/** Общий таймер подхода приложения. */
export const setTimer = createSetTimer()

let interval: ReturnType<typeof setInterval> | null = null
let subscribers = 0

function subscribeTicking(listener: () => void): () => void {
  const unsubscribe = setTimer.subscribe(listener)
  subscribers += 1
  if (interval === null) {
    setTimer.tick()
    interval = setInterval(() => setTimer.tick(), 250)
  }
  return () => {
    unsubscribe()
    subscribers -= 1
    if (subscribers === 0 && interval !== null) {
      clearInterval(interval)
      interval = null
    }
  }
}

/** Состояние таймера подхода для экрана; пока кто-то смотрит, таймер тикает. */
export function useSetTimer(): SetTimerSnapshot {
  return useSyncExternalStore(subscribeTicking, setTimer.snapshot, setTimer.snapshot)
}
